'use client'

import { useMemo } from 'react'

import { useSession } from 'next-auth/react'

import { useAvatarCatalog } from '@/features/profiles/hooks/useAvatarCatalog'
import { getCosmeticPreviewUrlsFromProfile } from '@/features/profiles/lib/cosmetic-preview-urls'
import { cn } from '@/shared/lib/utils'
import {
  AvatarComposite,
  type AvatarCompositeSize
} from '@/shared/ui/AvatarComposite'

const fallbackImgClass: Record<AvatarCompositeSize, string> = {
  xs: 'size-7',
  sm: 'size-10',
  md: 'h-[76px] w-[76px] sm:h-[84px] sm:w-[84px]',
  lg: 'mx-auto aspect-square w-full max-w-[240px] object-contain p-10 opacity-80'
}

export function UserCosmeticAvatar({
  size = 'xs',
  className,
  fallbackSrc = '/icons/user.svg',
  'aria-label': ariaLabel = 'Abrir perfil'
}: {
  size?: AvatarCompositeSize
  className?: string
  fallbackSrc?: string
  'aria-label'?: string
}) {
  const { status } = useSession()
  const enabled = status === 'authenticated'
  const { data, isLoading } = useAvatarCatalog(enabled)

  const urls = useMemo(() => getCosmeticPreviewUrlsFromProfile(data), [data])

  if (!enabled || (isLoading && !data) || !urls) {
    return (
      <img
        src={fallbackSrc}
        alt=""
        className={cn(fallbackImgClass[size], className)}
        draggable={false}
        aria-hidden
      />
    )
  }

  return (
    <AvatarComposite
      backgroundUrl={urls.backgroundUrl}
      avatarUrl={urls.avatarUrl}
      frameUrl={urls.frameUrl}
      size={size}
      className={className}
      aria-label={ariaLabel}
    />
  )
}
